/**
 * The core of a connector call (`c.fetch`, §4 `connectorActions`, §14.59): checks what the app asks
 * for, builds `baseUrl + path` by string concatenation, attaches the connector's secret, makes the
 * request with a timeout and a size cap, and shapes the answer. No Convex context: the caller
 * authorises first (device or, later, app token) and passes the connector row in, so every caller
 * shares these rules. Unit-tested in convex/test/connectorFetch.test.ts with a fake `fetch`.
 *
 * The secret only ever goes into the outgoing header. It is redacted from every string that comes
 * back (error messages, response headers, text bodies) in case the upstream echoes it.
 */
import { fail, invalid } from './errors';

export const CONNECTOR_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type ConnectorMethod = (typeof CONNECTOR_METHODS)[number];

export const MAX_PATH_LENGTH = 2048;
export const MAX_REQUEST_BODY_BYTES = 256 * 1024;
export const MAX_RESPONSE_BYTES = 1024 * 1024;
export const FETCH_TIMEOUT_MS = 10_000;
const MAX_REQUEST_HEADERS = 32;
const MAX_HEADER_VALUE_LENGTH = 8192;
const USER_AGENT = 'BoltCircles-Connector/1.0';

/** Request headers an app may set besides `X-…` ones. Everything else is refused. */
export const ALLOWED_REQUEST_HEADERS = ['accept', 'accept-language', 'content-type', 'if-none-match', 'x-request-id'] as const;
const ALLOWED_SET = new Set<string>(ALLOWED_REQUEST_HEADERS);
/** `X-…` headers that would let an app claim to be someone else to the upstream. */
const REFUSED_X_HEADER_RE = /^x-(forwarded(-.*)?|real-ip)$/;
/** RFC 9110 `token`: what a header field name may be. */
const HEADER_TOKEN_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
/** Response headers that describe framing or the upstream's cookies, never passed to the app. */
const DROPPED_RESPONSE_HEADERS = new Set([
  'set-cookie',
  'set-cookie2',
  'connection',
  'keep-alive',
  'proxy-connection',
  'transfer-encoding',
  'content-encoding',
  'content-length',
  'trailer',
  'upgrade',
]);
/** Also keeps response header names usable as Convex object keys (no leading `$` or `_`). */
const RESPONSE_HEADER_NAME_RE = /^[a-z0-9][a-z0-9!#%&'*+\-.^_`|~]*$/;
/** Plain http is for a local echo server only (F3, `normalizeBaseUrl`). */
const HTTP_HOSTS = new Set(['localhost', '127.0.0.1']);
const PRIVATE_SUFFIXES = ['.localhost', '.local', '.internal', '.home.arpa'];

export type ConnectorTarget = { name: string; baseUrl: string; authHeader: string; secret: string };
export type ConnectorRequest = { path: unknown; method?: unknown; headers?: Record<string, string>; body?: unknown };
export type ConnectorResponse = { status: number; headers: Record<string, string>; body: string; encoding: 'utf8' | 'base64' };
export type FetchImpl = (url: string, init: RequestInit) => Promise<Response>;
export type FetchLimits = { timeoutMs: number; maxResponseBytes: number };
export const DEFAULT_FETCH_LIMITS: FetchLimits = { timeoutMs: FETCH_TIMEOUT_MS, maxResponseBytes: MAX_RESPONSE_BYTES };

// ---------- the base URL's host ----------

function ipv4Parts(host: string): number[] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!m) return null;
  const parts = m.slice(1).map(Number);
  return parts.every((p) => p <= 255) ? parts : null;
}

function isPrivateIpv4([a = 0, b = 0, c = 0]: number[]): boolean {
  return (
    a === 0 || // "this network"
    a === 10 ||
    a === 127 || // loopback
    (a === 100 && b >= 64 && b <= 127) || // carrier-grade NAT
    (a === 169 && b === 254) || // link-local (cloud metadata lives here)
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 192 && b === 0 && c === 0) || // IETF protocol assignments
    (a === 198 && (b === 18 || b === 19)) || // benchmarking
    a >= 224 // multicast, reserved, broadcast
  );
}

/** The 8 groups of an IPv6 address as the URL parser prints it (compressed, no embedded IPv4); null if not one. */
function ipv6Groups(host: string): number[] | null {
  if (!/^[0-9a-f:]+$/.test(host) || !host.includes(':')) return null;
  const halves = host.split('::');
  if (halves.length > 2) return null;
  const parse = (s: string) => (s === '' ? [] : s.split(':').map((g) => parseInt(g, 16)));
  const head = parse(halves[0]!);
  const tail = halves.length === 2 ? parse(halves[1]!) : [];
  const fill = halves.length === 2 ? 8 - head.length - tail.length : 0;
  const groups = [...head, ...Array<number>(Math.max(fill, 0)).fill(0), ...tail];
  return groups.length === 8 && groups.every((g) => Number.isInteger(g) && g >= 0 && g <= 0xffff) ? groups : null;
}

function isPrivateIpv6(g: number[]): boolean {
  const [g0 = 0, g1 = 0, g2 = 0, g3 = 0, g4 = 0, g5 = 0, g6 = 0, g7 = 0] = g;
  const embeddedV4 = [g6 >> 8, g6 & 0xff, g7 >> 8, g7 & 0xff];
  const zeroPrefix = g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0;
  if (zeroPrefix && g5 === 0) return true; // ::, ::1 and the deprecated IPv4-compatible ::a.b.c.d
  if (zeroPrefix && g5 === 0xffff) return isPrivateIpv4(embeddedV4); // IPv4-mapped
  if (g0 === 0x64 && g1 === 0xff9b && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0) return isPrivateIpv4(embeddedV4); // NAT64
  return (g0 & 0xfe00) === 0xfc00 || (g0 & 0xffc0) === 0xfe80 || (g0 & 0xff00) === 0xff00; // unique local, link-local, multicast
}

/**
 * Why a connector's base URL may not be called, or null when it may. Nothing is resolved: only the
 * URL itself is judged. Refused: IP literals in private, loopback, link-local, CGNAT, multicast and
 * reserved ranges (IPv4 and IPv6, mapped forms included), `localhost` and `.localhost`, `.local`,
 * `.internal`, `.home.arpa` names, and single-label hosts. Plain `http://` is allowed only for
 * `localhost` / `127.0.0.1` (a local echo server, F3); `https://` to those is refused.
 */
export function connectorHostProblem(baseUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return 'not an absolute URL';
  }
  const host = url.hostname.toLowerCase().replace(/\.$/, '');
  if (url.protocol === 'http:') return HTTP_HOSTS.has(host) ? null : 'plain http:// to a host other than localhost';
  if (url.protocol !== 'https:') return `a ${url.protocol} URL`;
  if (host.startsWith('[')) {
    const groups = ipv6Groups(host.slice(1, -1));
    return !groups || isPrivateIpv6(groups) ? `a private address (${host})` : null;
  }
  const v4 = ipv4Parts(host);
  if (v4) return isPrivateIpv4(v4) ? `a private address (${host})` : null;
  if (host === 'localhost' || PRIVATE_SUFFIXES.some((s) => host.endsWith(s))) return `a local network name (${host})`;
  if (!host.includes('.')) return `a single-label host name (${host})`;
  return null;
}

// ---------- the request ----------

/**
 * The app's path: one leading `/`, at most 2 KB, no `//`, no `.` or `..` segments (also as `%2e`),
 * no backslashes, control characters or `#`. A query string is allowed and only checked for
 * control characters and backslashes.
 */
export function checkPath(path: unknown): string {
  if (typeof path !== 'string' || path.length === 0) invalid('path must be a string starting with "/", e.g. "/forecast?city=Krakow"');
  if (path.length > MAX_PATH_LENGTH) invalid(`path must be at most ${MAX_PATH_LENGTH} characters`);
  if (/[\x00-\x1f\x7f]/.test(path)) invalid('path must not contain control characters (encode them with encodeURIComponent)');
  if (path.includes('\\')) invalid('path must not contain a backslash');
  if (path.includes('#')) invalid('path must not contain a fragment (#)');
  const q = path.indexOf('?');
  const pathname = q === -1 ? path : path.slice(0, q);
  if (!pathname.startsWith('/')) invalid('path must start with "/" (it is appended to the connector\'s base URL)');
  if (pathname.includes('//')) invalid('path must not contain "//"');
  for (const segment of pathname.split('/')) {
    const dots = segment.replace(/%2e/gi, '.');
    if (dots === '.' || dots === '..') invalid('path must not contain "." or ".." segments');
  }
  return path;
}

export function checkMethod(method: unknown): ConnectorMethod {
  if (method === undefined || method === null) return 'GET';
  const upper = typeof method === 'string' ? method.toUpperCase() : '';
  if (!(CONNECTOR_METHODS as readonly string[]).includes(upper)) invalid(`method must be one of ${CONNECTOR_METHODS.join(', ')}`);
  return upper as ConnectorMethod;
}

/** Whether an app may set this (lower-case) header at all. The connector's own header is handled before. */
export function isAllowedRequestHeader(lower: string): boolean {
  return ALLOWED_SET.has(lower) || (lower.startsWith('x-') && !REFUSED_X_HEADER_RE.test(lower));
}

/**
 * The outgoing headers: the app's allowlisted ones (names lower-cased), a User-Agent, and the
 * connector's header with the secret. An app header with the connector's header name, in any case,
 * is dropped: the connector's value always wins and the app never learns or replaces it. Any other
 * header outside the allowlist (`Authorization`, `Cookie`, `Host`, hop-by-hop, …) is `INVALID`.
 */
export function buildRequestHeaders(appHeaders: Record<string, string> | undefined, authHeader: string, secret: string): Record<string, string> {
  const out: Record<string, string> = {};
  const auth = authHeader.toLowerCase();
  const entries = Object.entries(appHeaders ?? {});
  if (entries.length > MAX_REQUEST_HEADERS) invalid(`at most ${MAX_REQUEST_HEADERS} headers`);
  for (const [name, value] of entries) {
    if (!HEADER_TOKEN_RE.test(name)) invalid(`header name ${JSON.stringify(name)} is not a valid HTTP header name`);
    const lower = name.toLowerCase();
    if (lower === auth) continue;
    if (!isAllowedRequestHeader(lower)) {
      invalid(`header "${name}" cannot be set by an app; allowed: Accept, Accept-Language, Content-Type, If-None-Match, X-Request-Id and other X-… headers (the connector adds its own credential)`);
    }
    if (typeof value !== 'string' || /[\r\n\0]/.test(value) || value.length > MAX_HEADER_VALUE_LENGTH) {
      invalid(`header "${name}" must be a string of at most ${MAX_HEADER_VALUE_LENGTH} characters without line breaks`);
    }
    out[lower] = value;
  }
  if (auth !== 'user-agent') out['user-agent'] = USER_AGENT;
  out[authHeader] = secret;
  return out;
}

/** A string body for any method but GET, at most 256 KB of UTF-8. */
export function checkBody(method: ConnectorMethod, body: unknown): string | undefined {
  if (body === undefined || body === null) return undefined;
  if (typeof body !== 'string') invalid('body must be a string (the SDK turns an object into JSON)');
  if (method === 'GET') invalid('a GET request cannot have a body; use POST, PUT, PATCH or DELETE');
  if (new TextEncoder().encode(body).length > MAX_REQUEST_BODY_BYTES) invalid(`body must be at most ${formatBytes(MAX_REQUEST_BODY_BYTES)}`);
  return body;
}

/**
 * `baseUrl + path`, concatenated as strings (never `new URL(path, base)`, which lets `//host` change
 * the host), then parsed to make sure the origin and the base path survived.
 */
export function connectorUrl(baseUrl: string, path: string): string {
  const target = `${baseUrl}${checkPath(path)}`;
  const base = new URL(baseUrl);
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    invalid('path does not make a valid URL with the connector\'s base URL');
  }
  const basePath = base.pathname.replace(/\/$/, '');
  if (url.origin !== base.origin || !(url.pathname === basePath || url.pathname.startsWith(`${basePath}/`))) {
    invalid('path must stay under the connector\'s base URL');
  }
  return target;
}

export type PreparedRequest = { url: string; init: { method: ConnectorMethod; headers: Record<string, string>; body?: string } };

/** Every check on the app's side of the call, in one place; throws `INVALID` with the rule. */
export function prepareConnectorRequest(target: ConnectorTarget, req: ConnectorRequest): PreparedRequest {
  const method = checkMethod(req.method);
  const url = connectorUrl(target.baseUrl, checkPath(req.path));
  const body = checkBody(method, req.body);
  const headers = buildRequestHeaders(req.headers, target.authHeader, target.secret);
  return { url, init: { method, headers, ...(body !== undefined ? { body } : {}) } };
}

// ---------- the response ----------

/**
 * The secret, and the credential part of a `<scheme> <credential>` value such as `Bearer sk-…`,
 * replaced by `…`. Values shorter than 4 characters are left alone (they would mangle the text and
 * are no real credential).
 */
export function redactSecret(text: string, secret: string): string {
  let out = text;
  if (secret.length >= 4) out = out.replaceAll(secret, '…');
  const credential = /^[A-Za-z][\w.-]*\s+(\S{4,})$/.exec(secret)?.[1];
  if (credential) out = out.replaceAll(credential, '…');
  return out;
}

/** Text media types come back as UTF-8 strings; everything else as base64. */
export function isTextContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  const media = (contentType.split(';')[0] ?? '').trim().toLowerCase();
  return (
    media.startsWith('text/') ||
    media.endsWith('+json') ||
    media.endsWith('+xml') ||
    [
      'application/json',
      'application/xml',
      'application/javascript',
      'application/ecmascript',
      'application/x-www-form-urlencoded',
      'application/x-ndjson',
      'application/graphql',
    ].includes(media)
  );
}

/** Upstream headers for the app: lower-case names, cookies and framing headers dropped, the secret redacted. */
export function responseHeaders(headers: Headers, secret: string): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, name) => {
    const lower = name.toLowerCase();
    if (DROPPED_RESPONSE_HEADERS.has(lower) || !RESPONSE_HEADER_NAME_RE.test(lower)) return;
    out[lower] = redactSecret(value, secret);
  });
  return out;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64[a >> 2]! + B64[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    out += b === undefined ? '=' : B64[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    out += c === undefined ? '=' : B64[c & 63]!;
  }
  return out;
}

/** UTF-8 text for text media types (and for an untyped body that decodes cleanly), else base64. */
export function encodeBody(bytes: Uint8Array, contentType: string | null, secret: string): { body: string; encoding: 'utf8' | 'base64' } {
  if (isTextContentType(contentType)) return { body: redactSecret(new TextDecoder().decode(bytes), secret), encoding: 'utf8' };
  if (!contentType) {
    try {
      return { body: redactSecret(new TextDecoder('utf-8', { fatal: true }).decode(bytes), secret), encoding: 'utf8' };
    } catch {
      // not text
    }
  }
  return { body: bytesToBase64(bytes), encoding: 'base64' };
}

export function formatBytes(n: number): string {
  return n >= 1024 * 1024 ? `${n / 1024 / 1024} MB` : n >= 1024 ? `${n / 1024} KB` : `${n} bytes`;
}

/** The body, or null once it is longer than `max` bytes (the rest is not read). */
export async function readCapped(res: Response, max: number): Promise<Uint8Array | null> {
  const declared = Number(res.headers.get('content-length'));
  if (Number.isFinite(declared) && declared > max) {
    await res.body?.cancel().catch(() => {});
    return null;
  }
  if (!res.body) {
    const bytes = new Uint8Array(await res.arrayBuffer());
    return bytes.length > max ? null : bytes;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.length;
    if (total > max) {
      await reader.cancel().catch(() => {});
      return null;
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/**
 * Makes the call. Redirects are not followed (a 3xx comes back to the app as is), so the credential
 * never travels to a host the owner did not configure. `INVALID` for a bad request, `FORBIDDEN` for
 * a base URL on a private network, `UPSTREAM_TIMEOUT` after `timeoutMs` (connect and body),
 * `UPSTREAM_TOO_LARGE` past `maxResponseBytes`, `UPSTREAM_FAILED` when the upstream cannot be reached.
 * An HTTP error status is not an error: it is returned like any other.
 */
export async function performConnectorFetch(
  target: ConnectorTarget,
  req: ConnectorRequest,
  fetchImpl: FetchImpl,
  limits: FetchLimits = DEFAULT_FETCH_LIMITS,
): Promise<ConnectorResponse> {
  const problem = connectorHostProblem(target.baseUrl);
  if (problem) {
    fail('FORBIDDEN', `Connector "${target.name}" points at ${problem}, which connectors may not call; the owner changes its base URL in the builder → Connectors`);
  }
  const { url, init } = prepareConnectorRequest(target, req);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), limits.timeoutMs);
  const seconds = Math.round(limits.timeoutMs / 100) / 10;
  try {
    let res: Response;
    let bytes: Uint8Array | null;
    try {
      res = await fetchImpl(url, { ...init, redirect: 'manual', signal: controller.signal });
      bytes = await readCapped(res, limits.maxResponseBytes);
    } catch (err) {
      if (controller.signal.aborted) fail('UPSTREAM_TIMEOUT', `Connector "${target.name}" did not answer within ${seconds} s`);
      const reason = err instanceof Error ? err.message : String(err);
      fail('UPSTREAM_FAILED', redactSecret(`Could not reach connector "${target.name}": ${reason}`, target.secret));
    }
    if (!bytes) fail('UPSTREAM_TOO_LARGE', `Connector "${target.name}" answered with more than ${formatBytes(limits.maxResponseBytes)}`);
    return {
      status: res.status,
      headers: responseHeaders(res.headers, target.secret),
      ...encodeBody(bytes, res.headers.get('content-type'), target.secret),
    };
  } finally {
    clearTimeout(timer);
  }
}
